import { useEffect, useState } from "react";
import { Copy, Link2, RefreshCw, Trash2 } from "lucide-react";
import { api, json } from "../api";
import { Modal, Spinner, dateLabel } from "../components";
import type { Scene } from "./types";
type Share = {
  id: string;
  title: string;
  sceneId: string;
  url: string;
  createdAt: string;
  updatedAt: string;
};
type Props = {
  projectId: string;
  scene: Scene;
  scenes: Scene[];
  readOnly: boolean;
  /** Saves the scene, so a new or refreshed link has the latest edits. */
  flush: () => Promise<boolean>;
  close: () => void;
};
// Links to play the prototype without an account. Each one keeps a copy of
// the scenes from when it was made or last refreshed.
export function ShareDialog({
  projectId,
  scene,
  scenes,
  readOnly,
  flush,
  close,
}: Props) {
  const [shares, setShares] = useState<Share[] | null>(null),
    [title, setTitle] = useState(scene.title),
    [busy, setBusy] = useState(""),
    [error, setError] = useState(""),
    [copied, setCopied] = useState("");
  const path = `/projects/${projectId}/shares`,
    full = (s: Share) => location.origin + s.url;
  useEffect(() => {
    api<Share[]>(path)
      .then(setShares)
      .catch((e) => setError((e as Error).message));
  }, [path]);
  async function run(key: string, task: () => Promise<void>) {
    setBusy(key);
    setError("");
    try {
      await task();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }
  const create = () =>
    run("create", async () => {
      if (!(await flush())) return;
      const s = await api<Share>(
        path,
        json("POST", { sceneId: scene.id, title: title.trim() || scene.title }),
      );
      setShares((list) => [s, ...(list || [])]);
    });
  const refresh = (s: Share) =>
    run(s.id, async () => {
      if (!(await flush())) return;
      const next = await api<Share>(`${path}/${s.id}/refresh`, json("POST", {}));
      setShares((list) => list?.map((x) => (x.id === s.id ? next : x)) || []);
    });
  const revoke = (s: Share) => {
    if (!confirm(`Отозвать ссылку «${s.title}»? Она перестанет открываться.`))
      return;
    void run(s.id, async () => {
      await api(`${path}/${s.id}`, json("DELETE", {}));
      setShares((list) => list?.filter((x) => x.id !== s.id) || []);
    });
  };
  async function copy(s: Share) {
    try {
      await navigator.clipboard.writeText(full(s));
      setCopied(s.id);
      setTimeout(() => setCopied(""), 1500);
    } catch {
      setError("Не удалось скопировать. Выдели ссылку и скопируй вручную.");
    }
  }
  return (
    <Modal title="Поделиться прототипом" close={close}>
      <p className="small-text muted">
        По ссылке прототип можно открыть и поиграть без аккаунта, но не
        редактировать. Ссылка показывает сцены такими, какими они были при
        создании; кнопка «Обновить» выкладывает текущие правки по тому же
        адресу.
      </p>
      {!readOnly && (
        <div className="share-create">
          <input
            aria-label="Название ссылки"
            value={title}
            maxLength={160}
            onChange={(e) => setTitle(e.target.value)}
          />
          <button
            className="primary"
            disabled={!!busy}
            onClick={() => void create()}
          >
            <Link2 size={14} />
            Создать ссылку
          </button>
        </div>
      )}
      {error && (
        <p className="error-message" role="alert">
          {error}
        </p>
      )}
      {!shares ? (
        <Spinner />
      ) : shares.length ? (
        <ul className="share-list" aria-label="Ссылки на прототип">
          {shares.map((s) => (
            <li key={s.id} className="share-item">
              <div className="share-item-head">
                <strong>{s.title}</strong>
                <span className="small-text muted">
                  {scenes.find((x) => x.id === s.sceneId)?.title ??
                    "сцена удалена"}{" "}
                  · обновлена {dateLabel(s.updatedAt)}
                </span>
              </div>
              <div className="share-actions">
                <input
                  readOnly
                  aria-label={`Адрес ссылки ${s.title}`}
                  value={full(s)}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  className="secondary"
                  onClick={() => void copy(s)}
                >
                  <Copy size={13} />
                  {copied === s.id ? "Скопировано" : "Копировать"}
                </button>
                {!readOnly && (
                  <button
                    className="secondary"
                    disabled={!!busy}
                    aria-label={`Обновить ссылку ${s.title}`}
                    onClick={() => void refresh(s)}
                  >
                    <RefreshCw size={13} />
                    Обновить
                  </button>
                )}
                <button
                  className="icon-button"
                  disabled={!!busy}
                  aria-label={`Отозвать ссылку ${s.title}`}
                  onClick={() => revoke(s)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="small-text muted">Ссылок пока нет.</p>
      )}
    </Modal>
  );
}
